import { supabase } from '../../config/supabase';
import { Notification } from '../../types/notification';
import logger from '../../utils/logger';
import { notificationQueueService } from '../queue/notificationQueue';
import { notificationSyncService } from '../sync/notificationSyncService';
import { eventsService } from '../events.service';

/**
 * Convertit un objet notification du format Supabase vers le format de l'application
 * @param data Données de la notification au format Supabase
 * @returns Notification au format de l'application
 */
function toAppFormat(data: any): Notification {
  return {
    id: data.id,
    title: data.title,
    message: data.message,
    type: data.type,
    status: data.status || 'unread',
    createdAt: data.created_at ? new Date(data.created_at) : new Date(),
    actionId: data.action_id,
    markerId: data.marker_id,
    markerCategory: data.marker_category,
    reminderDate: data.reminder_date ? new Date(data.reminder_date) : undefined,
    address: data.address,
    isActive: data.is_active,
    user_id: data.user_id, // Ajout du user_id
    agency_id: data.agency_id, // Ajout du agency_id
  };
}

/**
 * Convertit un objet notification du format de l'application vers le format Supabase
 * @param notification Données de la notification au format de l'application
 * @param userId ID de l'utilisateur associé à la notification
 * @param agencyId ID de l'agence associée à la notification (optionnel)
 * @returns Données de la notification au format Supabase
 */
function toSupabaseFormat(notification: Omit<Notification, 'id'>, userId: string, agencyId?: string): Record<string, any> {
  return {
    title: notification.title,
    message: notification.message,
    type: notification.type,
    status: notification.status,
    created_at: new Date().toISOString(),
    action_id: notification.actionId,
    marker_id: notification.markerId,
    marker_category: notification.markerCategory,
    reminder_date: notification.reminderDate ? notification.reminderDate.toISOString() : null,
    address: notification.address,
    is_active: notification.isActive !== undefined ? notification.isActive : true,
    user_id: userId,
    agency_id: agencyId,
  };
}

/**
 * Sauvegarde l'état d'une notification dans localStorage
 * @param id ID de la notification
 * @param status Statut de la notification ('read' ou 'unread')
 */
function saveNotificationState(id: string, status: 'read' | 'unread'): void {
  try {
    // Récupérer les états existants
    const savedStates = localStorage.getItem('notification_states');
    let states: Array<{ id: string, status: 'read' | 'unread' }> = [];
    
    if (savedStates) {
      states = JSON.parse(savedStates);
    }
    
    // Mettre à jour ou ajouter l'état
    const existingIndex = states.findIndex(state => state.id === id);
    if (existingIndex >= 0) {
      states[existingIndex].status = status;
    } else {
      states.push({ id, status });
    }
    
    // Sauvegarder les états
    localStorage.setItem('notification_states', JSON.stringify(states));
    logger.info(`État de la notification ${id} sauvegardé dans localStorage: ${status}`);
  } catch (error) {
    logger.error(`Erreur lors de la sauvegarde de l'état de la notification ${id}:`, error);
  }
}

export const notificationsService = {
  /**
   * Récupère toutes les notifications depuis Supabase
   * @param filterUserId ID de l'utilisateur pour filtrer les notifications (optionnel)
   * @returns Une promesse qui résout avec un tableau de notifications
   */
  async getAll(filterUserId?: string | null): Promise<Notification[]> {
    try {
      // Traiter la file d'attente avant de récupérer les notifications
      await this.processQueue();
      
      // Vérifier et corriger les incohérences
      await notificationSyncService.checkAndFixInconsistencies();
      
      // Récupérer l'utilisateur courant
      const { data: userData } = await supabase.auth.getUser();
      if (!userData || !userData.user) {
        logger.error('Utilisateur non authentifié');
        return [];
      }
      
      // Récupérer le profil utilisateur pour vérifier s'il est admin/propriétaire
      const { data: userProfile } = await supabase
        .from('users')
        .select('is_owner, role')
        .eq('id', userData.user.id)
        .single();
      
      const isAdmin = userProfile?.is_owner || userProfile?.role === 'admin';
      logger.info(`Utilisateur ${userData.user.id} est ${isAdmin ? 'admin/propriétaire' : 'utilisateur standard'}`);
      
      // Préparer la requête de base
      let query = supabase
        .from('notifications')
        .select(`
          *,
          markers:marker_id (
            address
          )
        `)
        .order('created_at', { ascending: false });
      
      // Déterminer l'ID de l'utilisateur à filtrer
      // Si filterUserId est spécifié, l'utiliser pour le filtrage
      // Sinon, utiliser l'ID de l'utilisateur connecté
      const userIdToFilter = filterUserId !== undefined ? filterUserId : userData.user.id;
      
      // Si l'utilisateur est admin et que filterUserId est null (option "tous les utilisateurs"),
      // ne pas appliquer de filtre pour voir toutes les notifications
      if (isAdmin && userIdToFilter === null) {
        logger.info(`Aucun filtrage appliqué pour l'administrateur ${userData.user.id} (vue de toutes les notifications)`);
        logger.debug(`Requête complète: FROM notifications SELECT * ORDER BY created_at DESC`);
      } else {
        // Sinon, filtrer par user_id
        const idToFilter = userIdToFilter || userData.user.id;
        query = query.eq('user_id', idToFilter);
        
        logger.info(`Filtrage des notifications pour l'utilisateur ${idToFilter}`);
        logger.debug(`Requête complète: FROM notifications SELECT * WHERE user_id = '${idToFilter}' ORDER BY created_at DESC`);
      }
      
      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const now = new Date();
      logger.info(`Récupération des notifications: ${data.length} notifications trouvées`);
      
      // Première passe: identifier les rappels à venir et leur statut original
      const upcomingReminderIds = new Set<string>();
      const processedNotifications = data.map(notification => {
        // Enrichir la notification avec l'adresse du marqueur si disponible
        if (notification.markers && notification.markers.address) {
          notification.address = notification.markers.address;
        }
        
        // Vérifier si c'est un rappel à venir
        if (notification.type === 'reminder' && notification.reminder_date) {
          const reminderDate = new Date(notification.reminder_date);
          
          // Si la date de rappel est dans le futur
          if (reminderDate > now) {
            logger.info(`Notification ${notification.id} est un rappel à venir (${notification.reminder_date})`);
            upcomingReminderIds.add(notification.id);
            
            // Conserver le statut original pour référence, mais ne pas le modifier encore
            return {
              ...notification,
              _isUpcoming: true, // Propriété temporaire pour le traitement
              _originalStatus: notification.status // Sauvegarder le statut original
            };
          }
        }
        return notification;
      });

      // Récupérer les états sauvegardés dans localStorage
      let parsedStates: Array<{ id: string, status: Notification['status'] }> = [];
      try {
        const savedStates = localStorage.getItem('notification_states');
        if (savedStates) {
          parsedStates = JSON.parse(savedStates);
          logger.info('États des notifications récupérés depuis localStorage:', parsedStates);
        }
      } catch (storageError) {
        logger.error('Erreur lors de la récupération des états depuis localStorage:', storageError);
      }
      
      // Deuxième passe: appliquer les états sauvegardés et préserver les rappels à venir
      const mergedNotifications = processedNotifications.map(notification => {
        // Si c'est un rappel à venir, préserver son état spécial
        if (notification._isUpcoming) {
          // Supprimer les propriétés temporaires
          const { _isUpcoming, _originalStatus, ...cleanNotification } = notification;
          
          // Conserver le statut original pour les rappels à venir
          // Cela garantit qu'ils restent dans l'onglet "upcoming" mais conservent leur statut réel
          return cleanNotification;
        }
        
        // Pour les autres notifications, appliquer les états sauvegardés
        const savedState = parsedStates.find(state => state.id === notification.id);
        if (savedState && savedState.status === 'read' && notification.status === 'unread') {
          logger.info(`Restauration de l'état 'read' pour la notification ${notification.id} depuis localStorage`);
          
          // Ajouter à la file d'attente pour synchroniser avec Supabase
          notificationQueueService.enqueue(notification.id, 'markAsRead');
          
          return { ...notification, status: 'read' as Notification['status'] };
        }
        
        return notification;
      });
      
      // Convertir au format de l'application et retourner
      const result = mergedNotifications.map(toAppFormat);
      logger.info(`Traitement terminé: ${result.length} notifications prêtes à être affichées`);
      
      return result;
    } catch (error) {
      logger.error('Erreur lors de la récupération des notifications:', error);
      throw error;
    }
  },

  /**
   * Crée une nouvelle notification
   * @param notification Les données de la notification à créer
   * @returns Une promesse qui résout avec la notification créée
   */
  async create(notification: Omit<Notification, 'id'>): Promise<Notification> {
    try {
      logger.info('Création d\'une nouvelle notification:', notification);
      
      // Récupérer l'utilisateur actuel
      const { data: userData } = await supabase.auth.getUser();
      if (!userData || !userData.user) {
        throw new Error('Utilisateur non authentifié');
      }
      
      // Récupérer l'agence de l'utilisateur
      const { data: userProfile } = await supabase
        .from('users')
        .select('agency_id')
        .eq('id', userData.user.id)
        .single();
      
      const agencyId = userProfile?.agency_id;
      
      // Préparer les données pour Supabase avec user_id et agency_id
      const supabaseData = toSupabaseFormat(notification, userData.user.id, agencyId);
      
      const { data, error } = await supabase
        .from('notifications')
        .insert(supabaseData)
        .select()
        .single();
      
      if (error) {
        logger.error('Erreur lors de la création de la notification:', error);
        throw error;
      }
      
      const createdNotification = toAppFormat(data);
      
      // Émettre l'événement NOTIFICATION_CREATED pour informer les composants
      eventsService.emit('NOTIFICATION_CREATED', { notification: createdNotification });
      logger.info('Événement NOTIFICATION_CREATED émis', { notificationId: createdNotification.id });
      
      // Sauvegarder l'état initial dans localStorage
      saveNotificationState(createdNotification.id, createdNotification.status);
      
      logger.info('Notification créée avec succès:', createdNotification);
      return createdNotification;
    } catch (error) {
      logger.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  },

  /**
   * Met à jour une notification existante
   * @param id L'ID de la notification à mettre à jour
   * @param notification Les données à mettre à jour
   * @returns Une promesse qui résout avec la notification mise à jour
   */
  async update(id: string, notification: Partial<Notification>): Promise<Notification> {
    try {
      logger.info(`Mise à jour de la notification ${id}:`, notification);
      
      // Convertir les données au format Supabase
      const updateData: Record<string, any> = {};
      
      if (notification.title !== undefined) updateData.title = notification.title;
      if (notification.message !== undefined) updateData.message = notification.message;
      if (notification.type !== undefined) updateData.type = notification.type;
      if (notification.status !== undefined) {
        updateData.status = notification.status;
        // Sauvegarder l'état dans localStorage
        saveNotificationState(id, notification.status);
        
        // Ajouter à la file d'attente si c'est un changement de statut
        if (notification.status === 'read') {
          notificationQueueService.enqueue(id, 'update', { status: notification.status });
        }
      }
      
      updateData.updated_at = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('notifications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        logger.error(`Erreur lors de la mise à jour de la notification ${id}:`, error);
        throw error;
      }
      
      const updatedNotification = toAppFormat(data);
      
      // Si la mise à jour a réussi, retirer l'opération de la file d'attente
      if (notification.status === 'read') {
        notificationQueueService.dequeue(id, 'update');
      }
      
      logger.info(`Notification ${id} mise à jour avec succès:`, updatedNotification);
      return updatedNotification;
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour de la notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprime une notification
   * @param id L'ID de la notification à supprimer
   * @returns Une promesse qui résout lorsque la suppression est terminée
   */
  async delete(id: string): Promise<void> {
    try {
      logger.info(`Suppression de la notification ${id}...`);
      
      // Ajouter à la file d'attente
      notificationQueueService.enqueue(id, 'delete');
      
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);
      
      if (error) {
        logger.error(`Erreur lors de la suppression de la notification ${id}:`, error);
        throw error;
      }
      
      // Supprimer l'état du localStorage
      try {
        const savedStates = localStorage.getItem('notification_states');
        if (savedStates) {
          const states = JSON.parse(savedStates) as Array<{ id: string, status: 'read' | 'unread' }>;
          const newStates = states.filter(state => state.id !== id);
          localStorage.setItem('notification_states', JSON.stringify(newStates));
        }
      } catch (storageError) {
        logger.error(`Erreur lors de la suppression de l'état de la notification ${id} du localStorage:`, storageError);
      }
      
      // Si la suppression a réussi, retirer l'opération de la file d'attente
      notificationQueueService.dequeue(id, 'delete');
      
      logger.info(`Notification ${id} supprimée avec succès`);
    } catch (error) {
      logger.error(`Erreur lors de la suppression de la notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Marque une notification comme lue
   * @param id ID de la notification à marquer comme lue
   * @returns Une promesse qui résout lorsque l'opération est terminée
   */
  async markAsRead(id: string): Promise<void> {
    try {
      logger.info(`Marquage de la notification ${id} comme lue...`);
      
      // Sauvegarder l'état dans localStorage pour persistance
      try {
        const savedStates = localStorage.getItem('notification_states') || '[]';
        const parsedStates = JSON.parse(savedStates) as Array<{ id: string, status: Notification['status'] }>;
        
        // Vérifier si l'état existe déjà
        const existingStateIndex = parsedStates.findIndex(state => state.id === id);
        if (existingStateIndex >= 0) {
          parsedStates[existingStateIndex].status = 'read';
        } else {
          parsedStates.push({ id, status: 'read' });
        }
        
        localStorage.setItem('notification_states', JSON.stringify(parsedStates));
        logger.info(`État 'read' sauvegardé dans localStorage pour la notification ${id}`);
      } catch (storageError) {
        logger.error(`Erreur lors de la sauvegarde de l'état dans localStorage pour la notification ${id}:`, storageError);
      }
      
      // Ajouter l'opération à la file d'attente
      notificationQueueService.enqueue(id, 'markAsRead');
      
      // Tenter une mise à jour immédiate
      const now = new Date().toISOString();
      const updateData = { 
        status: 'read',
        updated_at: now
      };
      
      // D'abord, vérifier si la notification existe et est accessible
      const { data: existingNotif, error: checkError } = await supabase
        .from('notifications')
        .select('id, status, user_id')
        .eq('id', id)
        .maybeSingle();
      
      if (checkError) {
        logger.error(`Erreur lors de la vérification de la notification ${id}:`, checkError);
      } else if (!existingNotif) {
        logger.error(`❌ Notification ${id} introuvable ou inaccessible (problème RLS probable)`);
        logger.info(`L'opération reste dans la file d'attente pour une tentative ultérieure.`);
        return; // Sortir sans lever d'erreur
      } else {
        logger.info(`✅ Notification ${id} trouvée, statut actuel: ${existingNotif.status}, user_id: ${existingNotif.user_id}`);
      }
      
      const { data: updateResult, error } = await supabase
        .from('notifications')
        .update(updateData)
        .eq('id', id)
        .select();
      
      if (error) {
        logger.warn(`Erreur lors de la mise à jour de la notification ${id} dans Supabase:`, error);
        logger.info(`L'opération reste dans la file d'attente pour une tentative ultérieure.`);
        // Ne pas lever d'erreur ici, car l'opération est dans la file d'attente
      } else {
        logger.info(`Notification ${id} marquée comme lue dans Supabase avec succès.`, updateResult);
        
        // Vérifier que la mise à jour a bien été effectuée
        if (updateResult && updateResult.length > 0) {
          logger.info(`✅ Statut confirmé dans Supabase: ${updateResult[0].status}`);
        } else {
          logger.warn(`⚠️ Aucune ligne retournée après la mise à jour de la notification ${id} - Problème RLS probable`);
        }
        
        // Essayer également avec la fonction RPC pour être sûr
        try {
          await supabase.rpc('force_update_notification_status', {
            notification_id: id,
            new_status: 'read'
          });
          logger.info(`Notification ${id} forcée comme lue via RPC.`);
        } catch (rpcError) {
          logger.warn(`Erreur lors de l'appel RPC pour forcer la mise à jour de la notification ${id}:`, rpcError);
          // Ne pas lever d'erreur ici, car la mise à jour normale a déjà réussi
        }
      }
    } catch (error) {
      logger.error(`Erreur lors du marquage de la notification ${id} comme lue:`, error);
      throw error;
    }
  },
  
  /**
   * Traite la file d'attente des opérations de notification
   * @returns Une promesse qui résout lorsque le traitement est terminé
   */
  async processQueue(): Promise<void> {
    try {
      const queue = notificationQueueService.getQueue();
      if (queue.length === 0) {
        return;
      }
      
      logger.info(`Traitement de la file d'attente de notifications (${queue.length} opérations)...`);
      
      for (let i = 0; i < queue.length; i++) {
        const item = queue[i];
        
        try {
          if (item.operation === 'markAsRead') {
            const now = new Date().toISOString();
            const updateData = { 
              status: 'read',
              updated_at: now
            };
            
            // Tenter la mise à jour normale
            const { error } = await supabase
              .from('notifications')
              .update(updateData)
              .eq('id', item.id);
            
            if (error) {
              logger.warn(`Erreur lors du traitement de l'opération markAsRead pour ${item.id}:`, error);
              
              // Tenter avec la fonction RPC
              try {
                await supabase.rpc('force_update_notification_status', {
                  notification_id: item.id,
                  new_status: 'read'
                });
                logger.info(`Notification ${item.id} forcée comme lue via RPC.`);
                
                // Supprimer l'élément de la file d'attente
                notificationQueueService.dequeue(item.id, item.operation);
              } catch (rpcError) {
                logger.error(`Erreur lors de l'appel RPC pour forcer la mise à jour de la notification ${item.id}:`, rpcError);
                
                // Incrémenter le compteur de tentatives
                notificationQueueService.incrementRetryCount(item.id, item.operation);
              }
            } else {
              logger.info(`Opération markAsRead pour ${item.id} traitée avec succès.`);
              
              // Supprimer l'élément de la file d'attente
              notificationQueueService.dequeue(item.id, item.operation);
            }
          } else if (item.operation === 'delete') {
            const { error } = await supabase
              .from('notifications')
              .delete()
              .eq('id', item.id);
            
            if (error) {
              logger.warn(`Erreur lors du traitement de l'opération delete pour ${item.id}:`, error);
              
              // Incrémenter le compteur de tentatives
              notificationQueueService.incrementRetryCount(item.id, item.operation);
            } else {
              logger.info(`Opération delete pour ${item.id} traitée avec succès.`);
              
              // Supprimer l'élément de la file d'attente
              notificationQueueService.dequeue(item.id, item.operation);
            }
          }
        } catch (operationError) {
          logger.error(`Erreur lors du traitement de l'opération ${item.operation} pour ${item.id}:`, operationError);
          
          // Incrémenter le compteur de tentatives
          notificationQueueService.incrementRetryCount(item.id, item.operation);
        }
      }
      
      // Sauvegarder la file d'attente mise à jour
      notificationQueueService.saveQueue(queue);
      
      logger.info('Traitement de la file d\'attente terminé.');
    } catch (error) {
      logger.error('Erreur lors du traitement de la file d\'attente:', error);
    }
  },
};

